/**
 * 通讯模块（R0：私密通讯页 + 消息状态 fixture）。live 需适配既有 communicator 引擎，
 * client_message_id 作为幂等键，断网重发不产生重复消息。私密对话不进入星球圈。
 * 通讯器是“TA 的手机”（P2，用户 10-03 06:3x：「更应该像是一个手机那种展示的一个页面……家庭那个……更像是一个家庭的群……这个手机可能也会有一些小游戏」）：
 * - ?channel=phone 是手机首屏（./phone/PhoneHome）：聊天列表（我和 TA、家庭群）、TA 的朋友们、底下一排应用（朋友圈 /circle、小游戏 /play、朋友、相册）；
 *   信箱、地图上的通讯器按钮都进这里；
 * - 不带 channel 是“我和 TA”的聊天（“和 TA 说话”这类入口直接进来）；?channel=family 是家庭群（群顶栏、点开看群里都有谁，见 ./phone/FamilyGroup）；
 *   ?channel=friends 是 TA 的朋友（见 PetFriendList；一位朋友的详情是二级页 /communicator/friends/:friendId，见 PetFriendDetailPage）；
 *   ?channel=petchats 是 TA 和朋友的全部聊天，&thread= 是一场的回放（./phone/PetChatsSection：只读，主人旁观，不能插话）；
 * - 原来顶上那排分区按钮（我和 TA / 家庭来信 / TA 的朋友 / 朋友圈）撤了：聊天之间在手机首屏里切；聊天页左上返回，站内有上一页退回上一页，没有就回手机首屏。
 * 一个人住的家没有家庭群：TA 写给全家的旅途来信直接放进“我和 TA”（2531 R27 第 8 条）。
 * 公告（方案 7.1 / 11 节，GET /announcements）与星球活动（世界运营 2a，GET /events、/event-tells）：入口挪到手机首屏的屏幕下面
 * （AnnouncementStrip：有未读才亮一个小点，没有公告一行都不占；PlanetEventsEntry：永远同一句、不数、不上色——不推送）；全部公告、活动仍在全屏页 /announcements、/events。
 * 聊天两栏（我和 TA / 家庭群）整页锁在可见高度里，只有消息区自己滚（S11 PC-00）：高度见 useChatViewport，
 * 滚动位置、跟到底、“有新消息”见 ChatScroller；往上翻更早的消息见 useOlderMessages；手机首屏、“TA 的朋友”是普通长页面。
 */
import { Fragment, useEffect, useLayoutEffect, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { useIsMutating, useMutation, useMutationState, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams, useSearchParams } from "react-router";
import { useNow } from "@/shared/time/clock";
import { toApiError } from "@/shared/api/errors";
import { env } from "@/shared/config/env";
import { usePhoneListMemory } from "./phone/PhoneListMemory";
import { messageTime } from "./phone/phoneLines";
import type { MessageDeliveryState, MessageReadResult, MessageSummary, MessageThread } from "@/shared/contracts";
import { newIdempotencyKey } from "@/shared/api/idempotency";
import { defineModule } from "@/shared/modules/types";
import { queryKeys } from "@/shared/query/queryClient";
import { useServices } from "@/shared/services/registry";
import { useActiveHome, useCurrentHousehold } from "@/shared/session/householdContext";
import { usePrimaryTab } from "@/app/primaryTab";
import { Button, CHAT_SHELL_HANDLE, Chip, DataOriginBadge, EMPTY_ART, EmptyState, Icon, QueryView, setChatKeyboardOpen, TopBar } from "@/shared/ui";
import { fixtureThread } from "@/fixtures/social";
import { delay } from "@/fixtures/world";
import { WorldGate } from "@/features/world_map/WorldGate";
import { demoAnnouncementFeed, parseAnnouncementFeed } from "./announcements";
import { AnnouncementsPage } from "./AnnouncementsPage";
import { demoPlanetEventService, livePlanetEventService } from "./events.service";
import { ChatScroller } from "./ChatScroller";
import { MessagePhoto } from "./MessagePhoto";
import { PhotoAskStatusLine, photoAskStatusFor, type PhotoAskStatusText } from "./PhotoAskStatusLine";
import { usePhotoAskStatus } from "./usePhotoAskStatus";
import { chatMemoKey, forgetWithQueryCache, keepOnlyUser } from "./chatMemory";
import { ModelReplyNote } from "./ModelReplyNote";
import { ComeHomeBar, ComeHomeConfirm } from "./ComeHome";
import { PetFriendDetailPage } from "./PetFriendDetailPage";
import { PetFriendList } from "./PetFriendList";
import { PlanetEventsPage } from "./PlanetEventsPage";
import { FamilyGroupHeader, FamilyGroupMembers } from "./phone/FamilyGroup";
import { PetChatsSection, PET_CHATS_HREF } from "./phone/PetChatsSection";
import { PhoneHome } from "./phone/PhoneHome";
import { useFamilyGroup } from "./phone/useFamilyGroup";
import { foldFor, useChatViewport } from "./useChatViewport";
import { useMarkRead } from "./useMarkRead";
import { mergeThread, useOlderMessages, type OlderStatus } from "./useOlderMessages";
import { createSendReceiptJournal, SEND_JOURNAL_KEY, validSendIdentity, sameSendScope, originalSendReceipt, withSendReceiptLock, type SendScope, type SendPending } from "./sendReceiptJournal";
import "./communicator.css";
import { PrivateGameInvitation,PrivateGameInviteButton } from "./PrivateGameInvitation";
import { messageLinkTarget } from "./messageLink";

const STATE_TEXT: Record<MessageDeliveryState, string> = {
  sending: "发送中",
  delivered: "已送达 TA 的世界",
  awaiting_reply: "TA 晚点回你",
  processing: "照片冲洗中",
  failed: "没送出去",
};

/** 主要靠手指点的设备（手机、平板）：回车换行，只有点发送键才发 */
const touchFirst = () => typeof window.matchMedia === "function" && window.matchMedia("(pointer: coarse)").matches;

/**
 * 输入区：多行，随字数长高，最多 5 行（很矮的屏 3 行，见 communicator.css），再多在框里滚。
 * 回车：桌面上 Enter 发送、Shift+Enter 换行；手机上回车换行、点发送键才发；输入法组字时（isComposing 或 keyCode 229）回车绝不发送。
 */
function definitivelyRejected(value: unknown): boolean {
  const error = toApiError(value);
  return error.kind === "capability" || (error.kind === "http" && error.status !== null && error.status >= 400 && error.status < 500 && ![408, 409].includes(error.status));
}

/** Only known pre-commit rejection codes from the original send can release its marker.
 * Missing/unreadable envelopes become INTERNAL_ERROR in client.ts and stay unknown.
 * Capability errors, conflicts, throttling and timeouts never prove not_sent. */
function rejectedOriginalSend(value: unknown): boolean {
  const error = toApiError(value);
  if (error.kind !== "http" || error.status === null) return false;
  const codes: Record<number, readonly string[]> = {
    400: ["VALIDATION_FAILED"], 401: ["AUTH_REQUIRED", "SESSION_EXPIRED"],
    403: ["FORBIDDEN", "CSRF_FAILED"], 404: ["NOT_FOUND"], 422: ["VALIDATION_FAILED"],
  };
  if (!codes[error.status]?.includes(error.code)) return false;
  const details = error.details;
  if (details?.result_unknown || [details?.phase, details?.recovery_phase, details?.status, details?.reason].includes("result_unknown")) return false;
  if ((details?.phase !== undefined && details.phase !== "before_send")
      || (details?.recovery_phase !== undefined && details.recovery_phase !== "before_send")) return false;
  return true;
}

function SessionComposer({ petId, scope, onSent }: { petId: string; scope: string; onSent: () => void }) {
  const { communicator } = useServices();
  const queryClient = useQueryClient();
  const [text, setText] = useState("");
  const [clientId, setClientId] = useState(() => newIdempotencyKey("msg"));
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const mutationKey = ["communicator", "send", scope] as const;
  const unresolved = useMutationState({ filters: { mutationKey, exact: true }, select: (mutation) => mutation.state.status === "error" && !definitivelyRejected(mutation.state.error) }).some(Boolean);
  const pending = useIsMutating({ mutationKey, exact: true }) > 0;
  const send = useMutation({
    mutationKey,
    gcTime: Infinity, // Keep an unknown receipt for this app session, including page returns.
    retry: false,
    mutationFn: () => communicator.send(petId, { client_message_id: clientId, text }),
    onSuccess: () => {
      setText("");
      setClientId(newIdempotencyKey("msg"));
      onSent();
      void queryClient.invalidateQueries({ queryKey: ["communicator", "messages"] });
    },
  });
  const resultUnknown = unresolved;
  const submit = () => {
    if (text.trim() && !pending && !resultUnknown) send.mutate();
  };
  useLayoutEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight + el.offsetHeight - el.clientHeight}px`;
  }, [text]);
  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key !== "Enter") return;
    // 输入法正在组字：这一下回车是选字 / 上屏，绝不发送
    if (e.nativeEvent.isComposing || e.keyCode === 229) return;
    // Shift+Enter 换行；其余组合键不拦
    if (e.shiftKey || e.altKey || e.ctrlKey || e.metaKey) return;
    if (touchFirst()) return;
    e.preventDefault();
    submit();
  };
  return (
    <form
      className="ps-comm-composer"
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
    >
      {/* 私聊里不放任何拍照入口（用户 10-09「不要那个按钮……让宠物自主去判断是否要拍照给主人」，PHOTO-ASK-FLOW-01）：主人想要照片就自己说，拍不拍由 TA 决定 */}
      <textarea ref={inputRef} className="ps-input ps-comm-composer__input" rows={1} value={text} onChange={(e) => setText(e.target.value)} onKeyDown={onKeyDown} placeholder="给 TA 捎句话…" aria-label="消息内容" maxLength={500} />
      {/* 按下发送键时焦点留在输入框里：手机上键盘不收起再弹出 */}
      <Button type="submit" variant="primary" icon="mail" loading={send.isPending} disabled={resultUnknown || pending} aria-label="发送" onMouseDown={(e) => e.preventDefault()} />
      {/* 没送出去的提示：这一块一直在（空着不占高度），失败时才放字；role="status" 一直在的区块换字，读屏念得最稳；样式上单独占一行 */}
      <div className="ps-comm-composer__status" role="status">
        {resultUnknown || send.isError ? <Chip tone="danger">{resultUnknown ? "发送结果还未确认，请先查看来信，暂不重复发送" : "没送出去，再点发送就好。"}</Chip> : null}
        {resultUnknown ? <p className="ps-comm-composer__unknown048">读取来信不能确认这次发送；这里会继续保留发送锁。</p> : null}
      </div>
    </form>
  );
}

type ComposerIdentity = import("./sendReceiptJournal").SendIdentity;
function Composer(props: { petId: string; scope: string; identity: ComposerIdentity; onSent: () => void }) {
 // Synthetic demo keeps its existing in-session UI048 behavior; it is not an authenticated durable native journey.
 return env.dataMode === "fixture" ? <SessionComposer {...props} /> : <DurableComposer {...props} />;
}
function DurableComposer({ petId, identity, onSent }: { petId: string; identity: ComposerIdentity; onSent: () => void }) {
 const { communicator, session, households } = useServices();
 const queryClient = useQueryClient();
 const identityKey = JSON.stringify(identity);
 const journal = useMemo(() => createSendReceiptJournal(), []);
 const [view, setView] = useState(() => journal.inspect(identity));
 const [text, setText] = useState("");
 const [busy, setBusy] = useState(false);
 const [notice, setNotice] = useState("");
 const inputRef = useRef<HTMLTextAreaElement>(null);
 const life = useMemo(() => ({ alive: true, busy: false, epoch: 0 }), [identityKey, queryClient, communicator, session, households]);
 const returnInputAfterReceipt = useRef(false);
 const rejectedRetry = useRef<SendPending | null>(null);
 const refresh = () => setView(journal.inspect(identity));
 useLayoutEffect(() => {
  life.alive = true;
  const unsubscribe = queryClient.getQueryCache().subscribe(e => {
   const k = JSON.stringify(e.query.queryKey);
   const auth = k === JSON.stringify(queryKeys.session), family = k === JSON.stringify(queryKeys.households(identity.userId ?? "-"));
   if (!auth && !family) return;
   if (e.type === "removed") { life.epoch++; return; }
   if (e.type !== "updated") return; // Observer attachment/count changes are not an identity change.
   if (e.action.type === "error" || e.action.type === "setState") { life.epoch++; return; }
   if (e.action.type !== "success") return;
   if (auth) {
    const current = e.query.state.data as { authenticated?: boolean; user?: { user_id?: string } | null } | undefined;
    if (current?.authenticated !== true || current.user?.user_id !== identity.userId) life.epoch++;
   } else {
    const current = e.query.state.data as Array<{ household_id: string; home_id: string; pets: Array<{ pet_id: string }> }> | undefined;
    if (!Array.isArray(current) || !current.some(h => h.household_id === identity.householdId && h.home_id === identity.homeId && h.pets.some(p => p.pet_id === identity.petId))) life.epoch++;
   }
  });
  const changed = (e: StorageEvent) => { if (e.key === SEND_JOURNAL_KEY || e.key === null) refresh(); };
  window.addEventListener("storage", changed);
  return () => { life.alive = false; life.epoch++; unsubscribe(); window.removeEventListener("storage", changed); };
 }, [life, journal, identityKey]);
 const active = (epoch: number) => life.alive && life.epoch === epoch;
 const freshScope = async (epoch: number): Promise<SendScope> => {
  if (!validSendIdentity(identity) || !active(epoch)) throw Error("identity not confirmed");
  const [s, detail] = await Promise.all([session.current(), households.detail(identity.householdId!)]);
  if (!active(epoch) || !s.authenticated || s.user?.user_id !== identity.userId
   || detail.household.household_id !== identity.householdId || detail.household.home_id !== identity.homeId
   || !detail.household.home_activated || !detail.household.pets.some(p => p.pet_id === petId && p.join_step === "moved_in")
   || !detail.members.some(m => m.user_id === identity.userId) || !Number.isSafeInteger(detail.version) || detail.version < 0) throw Error("current scope not confirmed");
  return { userId: identity.userId!, petId, householdId: identity.householdId!, homeId: identity.homeId!, householdVersion: detail.version };
 };
 const confirmed = async (p: SendPending, receipt: unknown, epoch: number) => {
  if (!originalSendReceipt(receipt, p.clientMessageId) || !active(epoch)) throw Error("original positive receipt unavailable");
  const current = await freshScope(epoch);
  if (!sameSendScope(current, p) || !active(epoch) || !journal.resolve(current, p.clientMessageId)) throw Error("original scope no longer confirmed");
  if (!active(epoch)) return;
  rejectedRetry.current = null;
  returnInputAfterReceipt.current = true;
  setText(""); setNotice("这次发送的回执已确认；投递和照片状态请查看这条来信。");
  onSent(); void queryClient.invalidateQueries({ queryKey: ["communicator", "messages"] });
 };
 const run = async (recover: boolean) => {
  if (life.busy || !life.alive || (!recover && !text.trim())) return;
  life.busy = true; const epoch = life.epoch; setBusy(true); setNotice("");
  try {
   await withSendReceiptLock(identity, async () => {
    const current = await freshScope(epoch); if (!active(epoch)) return;
    const existing = journal.inspect(identity);
    if (recover) {
     if (existing.kind !== "pending" || !sameSendScope(current, existing.pending) || !communicator.receipt) throw Error("original read capability or version not confirmed");
     // Original private pet URL plus original client ID only. Lists/absence never clear this record.
     const receipt = await communicator.receipt(petId, existing.pending.clientMessageId);
     await confirmed(existing.pending, receipt, epoch);
    } else {
     if (existing.kind !== "clear") throw Error("original operation unresolved");
     const retry = rejectedRetry.current;
     const p = journal.begin(current, retry && sameSendScope(current, retry) ? retry.clientMessageId : newIdempotencyKey("msg")); refresh();
     if (!active(epoch)) return; // Durable pending survives a cancelled before-POST admission; never silently discarded.
     let receipt: MessageSummary;
     try {
      receipt = await communicator.send(petId, { client_message_id: p.clientMessageId, text });
     } catch (error) {
      // This catch wraps only the original POST, never subsequent receipt/identity verification.
      if (!rejectedOriginalSend(error) || !active(epoch)) throw error;
      const checked = await freshScope(epoch);
      const stored = journal.inspect(identity);
      if (!active(epoch) || !sameSendScope(checked, p) || stored.kind !== "pending"
          || stored.pending.clientMessageId !== p.clientMessageId || !sameSendScope(stored.pending, p)
          || !journal.resolve(checked, p.clientMessageId)) throw error;
      rejectedRetry.current = p; // Memory only; retain this rejected attempt's ID and current draft.
      returnInputAfterReceipt.current = true;
      setNotice("没送出去，再点发送就好。");
      return; // No automatic resend, no success callback or message projection.
     }
     await confirmed(p, receipt, epoch);
    }
   });
  } catch {
   if (active(epoch)) setNotice(recover ? "还没有确认到这次发送的结果，原编号继续保留，不会再次发送。" : "发送未能确认；请查看本机记录或查询原编号，暂不再次发送。");
  } finally { life.busy = false; if (life.alive) { setBusy(false); refresh(); } }
 };
 useLayoutEffect(() => {
  const el = inputRef.current; if (!el) return;
  el.style.height = "auto"; el.style.height = `${el.scrollHeight + el.offsetHeight - el.clientHeight}px`;
 }, [text]);
 const locked = view.kind !== "clear";
 useLayoutEffect(() => {
  if (!returnInputAfterReceipt.current || busy || view.kind !== "clear") return;
  const input = inputRef.current;
  if (input?.isConnected && !input.disabled) {
   if (document.activeElement === document.body) input.focus({ preventScroll: true });
   returnInputAfterReceipt.current = false;
  }
 }, [busy, view.kind]);
 const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
  if (e.key !== "Enter" || e.nativeEvent.isComposing || e.keyCode === 229 || e.shiftKey || e.altKey || e.ctrlKey || e.metaKey || touchFirst()) return;
  e.preventDefault(); if (!locked && !busy) void run(false);
 };
 return <form className="ps-comm-composer" onSubmit={e => { e.preventDefault(); if (!locked && !busy) void run(false); }}>
  <textarea ref={inputRef} className="ps-input ps-comm-composer__input" rows={1} value={text} onChange={e => setText(e.target.value)} onKeyDown={onKeyDown} placeholder="给 TA 捎句话…" aria-label="消息内容" maxLength={500} disabled={locked || busy} />
  <Button type="submit" variant="primary" icon="mail" loading={busy} disabled={locked || busy} aria-label="发送" onMouseDown={e => e.preventDefault()} />
  <div className="ps-comm-composer__status" role="status">
   {locked ? <Chip tone="danger">发送结果还未确认，原编号已保留，暂不重复发送</Chip> : null}
   {notice ? <p>{notice}</p> : null}
   {view.kind === "pending" ? <div className="ps-comm-composer__recovery5335"><p>只查询这次发送，不会把消息再发一次。</p><button type="button" disabled={busy} onClick={() => void run(true)}>查询这次发送结果</button></div> : null}
   {view.kind === "held" ? <div className="ps-comm-composer__recovery5335"><p>本机记录或当前身份尚未确认，不能安全发送。请保留记录。</p><button type="button" disabled={busy} onClick={refresh}>重新检查本机记录</button></div> : null}
  </div>
 </form>;
}

/**
 * 气泡下面那行小字。送达状态（发送中 / 已送达 TA 的世界 / TA 晚点回你 / 没送出去）只说主人发出的消息
 * （2026-09-24 巡检 P2：TA 发来的消息下面也写着“已送达 TA 的世界”）。TA 发来的消息不写送达状态：
 * 后端对它的 processing / failed 说的是随信的图，图的情况上面已经单独写了（随信的照片还在冲洗 / 没冲洗出来，信已经送到）。
 * 家庭频道的标记两边都留着；一个人住的家没有家庭分区，不写（channelTag=false）。
 */
function stateLine(m: MessageSummary, channelTag: boolean): string {
  const state = m.sender === "owner" ? m.status_note || STATE_TEXT[m.state] : "";
  return [state, channelTag && m.channel === "family" ? "家庭频道" : ""].filter(Boolean).join(" · ");
}

/** 气泡只靠左右和颜色分谁说的：给读屏补一句发言人（“我说：”“TA 说：”），看的人看不到（2026-09-26 巡检）。 */
const SPEAKER: Record<MessageSummary["sender"], string> = { owner: "我说：", pet: "TA 说：" };

/**
 * 家庭群里的一条（c84a 0020Z 视觉复审第 3 条）：气泡上写是谁说的（TA 的名字；万一有我发的写“我”），下面写几点。
 * 群里的消息都在这只宠物的通讯里（communicator/{pet_id}），所以说话的就是这一只；不编别的发言人。
 */
type GroupLine = { name: string; now: number };

/** 消息在对话里出现的时刻（同 MessageSummary.available_at 的约定：排队的回复按它排，没有就用写入时刻）。 */
export const shownAt = (m: MessageSummary): number => Date.parse(m.available_at ?? m.created_at) || 0;

function Bubble({ m, reply, hasGuide, hasCollection, fresh, channelTag = true, group, photoScope, onReadPhoto, readingPhoto, photoAskStatus = null, privateGameContext = false }: { privateGameContext?: boolean; m: MessageSummary; reply: MessageSummary | null; hasGuide: boolean; hasCollection: boolean; fresh?: boolean; channelTag?: boolean; group?: GroupLine; photoScope: string; onReadPhoto: () => Promise<boolean>; readingPhoto: boolean; photoAskStatus?: PhotoAskStatusText | null }) {
  const gameSource = m.source_event_id?.startsWith("game-invite:");
  const journeyId = gameSource ? undefined : m.source_event_id?.split(":", 1)[0];
  const linkTarget = messageLinkTarget(m.link);  // TA 的心愿来信点开去哪（UI-MSGLINK-01）：wish 去心愿页，plan 去这份计划
  const who = group ? (m.sender === "owner" ? "我" : group.name) : null;
  const line = [group ? messageTime(shownAt(m), group.now) : null, stateLine(m, channelTag) || null].filter(Boolean).join(" · ");
  return (
    <li className={`ps-msg ps-msg--${m.sender}${fresh ? " is-fresh" : ""}`} data-mid={m.message_id} data-motion={fresh ? "enter" : undefined}>
      <span className="visually-hidden">{who ? `${who}说：` : SPEAKER[m.sender]}</span>
      {who ? (
        <span className="ps-msg__who" aria-hidden="true">
          {who}
        </span>
      ) : null}
      {reply ? <div className="ps-msg__reply">回复「{reply.text}」</div> : m.reply_to ? <div className="ps-msg__reply">回复较早的一条消息 · 原文未加载</div> : null}
      <div className="ps-msg__text">{m.text}</div>
      <MessagePhoto message={m} scope={photoScope} onRead={onReadPhoto} reading={readingPhoto} />
      {m.source_event_id && !gameSource ? <div className="ps-msg__links"><span>来自这趟旅途</span>{hasGuide && journeyId ? <Link to={`/guides?journey=${encodeURIComponent(journeyId)}`}>这趟旅途的手账</Link> : null}{hasCollection ? <Link to={`/collection?source_event_id=${encodeURIComponent(m.source_event_id)}`}>相关收藏</Link> : null}</div> : null}
      {linkTarget ? <div className="ps-msg__links"><Link to={linkTarget.to}>{linkTarget.label}</Link></div> : null}
      {line ? <span className="ps-msg__state">{line}</span> : null}
      <PhotoAskStatusLine text={photoAskStatus} />
      {gameSource && privateGameContext ? <PrivateGameInvitation message={m} privateContext={privateGameContext} /> : null}
    </li>
  );
}

/**
 * 家庭群底下（输入框的位置）：群里不能发消息——发消息的接口不带分区，只进“我和 TA”。如实写只读，给去私聊的入口（c84a 0020Z 第 3 条：
 * 补可用输入或真实只读状态，不把家人面板算真实群聊）。
 * UI-PHONE-COPY-01（V-15、V-16）：「不能回复」只在有信时写（没信时空态那一句已经够了，不再四处重复）；去私聊的按钮和手机首屏同名「我和 X」。
 */
function FamilyReadOnly({ name, hasLetters }: { name: string; hasLetters: boolean }) {
  return (
    <div className="ps-comm-readonly" role="note">
      {hasLetters ? <p>这里是 {name} 寄给全家的信，这里不能回复。</p> : null}
      <Link className="ps-btn ps-btn--secondary ps-btn--sm" to="/communicator">
        我和 {name}
      </Link>
    </div>
  );
}

/**
 * 顶栏标题：共享 TopBar 的标题一行放不下时整行截断，320 宽、名字长时会把“的家庭来信”“的朋友”这些关键词截掉（2026-09-26 巡检）。
 * 这里只排通讯器自己传进去的字：看的那一份只让名字那一段出省略号，“我和”“的……”总在（见 communicator.css 的 .ps-comm-title）；读屏读旁边那句完整的。
 * 私聊叫「我和 X」，和手机首屏那一行同名（UI-PHONE-COPY-01，V-16：原来是「和 X 的通讯」，「通讯」一词退出这一页）。
 */
function CommTitle({ before, name, after }: { before: string; name: string; after: string }) {
  return (
    <>
      <span className="visually-hidden">{`${before}${name}${after}`}</span>
      <span className="ps-comm-title" aria-hidden="true">
        {before ? <span>{before}</span> : null}
        <span className="ps-comm-title__name">{name}</span>
        <span>{after}</span>
      </span>
    </>
  );
}

/** 消息区最上面：往上翻页到哪了（一直在的 status 区，读屏会念变化）。自动加载失败或停下后只有点“再试一次”才重来。 */
function OlderMarker({ status, hasMore, empty, onLoad }: { status: OlderStatus; hasMore: boolean; empty: boolean; onLoad: () => void }) {
  let body: ReactNode = null;
  if (status === "loading") body = <span>正在取更早的消息…</span>;
  else if (status === "failed") body = <><span>更早的消息没取到</span><button type="button" onClick={onLoad}>再试一次</button></>;
  else if (status === "stopped") body = <><span>更早的消息暂时取不到了</span><button type="button" onClick={onLoad}>再试一次</button></>;
  else if (hasMore) body = <button type="button" onClick={onLoad}>查看更早的消息</button>;
  else if (!empty) body = <span>没有更早的消息了</span>;
  return <div className="ps-comm-older" role="status">{body}</div>;
}

/** 页面分区沿用 ?channel=（各处已有 ?channel=family 的链接）；phone（手机首屏）、friends、petchats 不是消息频道，只借同一个参数。 */
type Section = "private" | "family" | "friends" | "phone" | "petchats";
const sectionOf = (raw: string | null): Section => (raw === "family" || raw === "friends" || raw === "phone" || raw === "petchats" ? raw : "private");
/** 手机首屏：信箱、地图上的通讯器按钮进这里；聊天页没有站内上一页时返回这里 */
export const PHONE_HOME = "/communicator?channel=phone";

function CommunicatorPage() {
  const { communicator, economy, transport } = useServices();
  const queryClient = useQueryClient();
  const { userId, pet, household } = useCurrentHousehold();
  // 外壳不再挂底栏（10-03）：左上角返回，回到 TA 此刻的主屏（在家小屋、在外地图）；站内有上一页时退回上一页
  const primary = usePrimaryTab();
  const [params] = useSearchParams();
  // 一个人住的家没有“家人”：TA 写给全家的旅途来信直接放进“我和 TA”，不另开“家庭来信”（2531 R27 第 8 条：TA 第一次出门的来信被拆进“家庭来信”，
  // 一个人住的玩家不会去点，以为 TA 出门后就没消息了）。各处 ?channel=family 的链接在这样的家里也落到“我和 TA”。演示模式没有家庭上下文，照旧分开。
  const solo = Boolean(household && household.member_count <= 1);
  const asked = sectionOf(params.get("channel"));
  const section: Section = solo && asked === "family" ? "private" : asked;
  const channel = section === "family" ? "family" : "private";
  // 家庭群：群名、群里的伙伴、家人（拿不到就是 null，只列伙伴）；手机首屏的群头像也用它
  const group = useFamilyGroup();
  const [membersOpen, setMembersOpen] = useState(false);
  const home = useActiveHome();
  // 宠物先从家庭名单里取（/households 一回来就有），取不到（演示模式没有家庭上下文）再退回家园快照：
  // 消息不必等 /home 回来才开始拉，和它并行（I 10-03 清单第 3 件：打开先灰屏好几秒，实测 session → households → home → messages 串着走）
  const petId = pet?.pet_id ?? home.data?.pet.pet_id;
  const identityReady = env.dataMode === "fixture" || Boolean(userId && pet);
  const identityKey = JSON.stringify([userId, household?.household_id, household?.home_id, petId]);
  // 看“TA 的朋友”、TA 和朋友的聊天时不轮询消息；切回来先显示缓存，再按过期规则刷新。手机首屏要写每个聊天的最后一句，照读。
  const thread = useQuery({ queryKey: queryKeys.messagesFor(userId ?? "-", petId ?? "-"), queryFn: () => communicator.thread(petId!), enabled: identityReady && Boolean(petId) && section !== "friends" && section !== "petchats", refetchInterval: 15_000 });
  const photoAsks = usePhotoAskStatus({ userId, petId, householdId: household?.household_id ?? null, homeId: household?.home_id ?? null, identityKey,
    enabled: identityReady && asked === "private" && section === "private" && !thread.isError && thread.data?.pet_id === petId });
  // A manual read is a local rendering opportunity, not a cache or permission grant.
  const photoReadLife = useMemo(() => ({ alive: true, busy: false, generation: 0, busyToken: 0 }), [identityKey, channel, section, identityReady, queryClient, communicator]);
  useLayoutEffect(() => {
    photoReadLife.alive = true;
    return () => { photoReadLife.alive = false; photoReadLife.generation++; };
  }, [photoReadLife]);
  const readPhoto = async (message: MessageSummary): Promise<boolean> => {
    const life = photoReadLife;
    if (!life.alive || life.busy || !identityReady || !petId || (section !== "private" && section !== "family")) return false;
    const key = queryKeys.messagesFor(userId ?? "-", petId);
    const cache = queryClient.getQueryCache(), query = cache.find({ queryKey: key, exact: true });
    if (!query || query.state.fetchStatus !== "idle") return false;
    const token = ++life.generation;
    life.busy = true; life.busyToken = token;
    let fetches = 0, success: MessageThread | undefined, successCount = -1;
    const householdHash = JSON.stringify(queryKeys.households(userId ?? "-"));
    const unsubscribe = cache.subscribe(event => {
      if (JSON.stringify(event.query.queryKey) === householdHash) {
        // Catch identity loss/return in one batched render while this GET is in flight.
        const houses = event.query.state.data as Array<{ household_id: string; home_id: string; pets: Array<{ pet_id: string }> }> | undefined;
        if (event.type === "removed" || event.query.state.status !== "success" || !Array.isArray(houses) || !houses.some(h => h.household_id === household?.household_id && h.home_id === household?.home_id && h.pets.some(p => p.pet_id === petId))) life.generation++;
        return;
      }
      if (event.query !== query) return;
      if (event.type === "removed") { life.generation++; return; }
      if (event.type !== "updated") return;
      const action = event.action;
      if (action.type === "fetch") {
        if (++fetches !== 1) life.generation++;
      } else if (action.type === "success") {
        if (action.manual) life.generation++;
        else if (fetches === 1) { success = query.state.data as MessageThread | undefined; successCount = query.state.dataUpdateCount; }
      } else if (action.type === "error" || action.type === "setState") {
        // cancelQueries can resolve refetch with reverted cached success. It is not this GET's success.
        life.generation++;
      }
    });
    try {
      if (asked === "private" && section === "private") void photoAsks.refresh();
      const result = await thread.refetch();
      if (!life.alive || life.generation !== token || result.isError || !result.isSuccess || fetches !== 1 || !success || result.data !== success) return false;
      if (cache.find({ queryKey: key, exact: true }) !== query || query.state.status !== "success" || query.state.fetchStatus !== "idle" || query.state.data !== success || query.state.dataUpdateCount !== successCount || success.pet_id !== petId) return false;
      const current = success.items.find(row => row.message_id === message.message_id);
      return Boolean(current && current.photo_url && (current.photo_status == null || current.photo_status === "ready") && current.photo_url === message.photo_url && (current.photo_status ?? null) === (message.photo_status ?? null) && (current.channel ?? "private") === (message.channel ?? "private"));
    } catch { return false; }
    finally {
      unsubscribe();
      if (life.busyToken === token) life.busy = false;
    }
  };
  // 往上翻更早的几页：带 cursor 取、后端不记已读；坏游标从最新一页重来（见 useOlderMessages）
  const older = useOlderMessages({ userId: userId ?? "-", petId, latest: thread.data, scope: identityKey, enabled: identityReady && !thread.isError && thread.data?.pet_id === petId, refetchLatest: async () => {
    const result = await thread.refetch();
    // A failed refetch may still carry cached data; it is not a fresh authorized read.
    if (result.isError) throw result.error;
    return result.data;
  } });
  const guides = useQuery({ queryKey: queryKeys.guidesFor(userId ?? "-", petId ?? "-"), queryFn: ({ signal }) => transport.guides(petId, signal), enabled: (section === "family" || (solo && section === "private")) && Boolean(petId && userId), retry: false });
  const collection = useQuery({ queryKey: queryKeys.collectionFor(userId ?? "-", petId ?? "-"), queryFn: ({ signal }) => economy.collection(petId, signal), enabled: (section === "family" || (solo && section === "private")) && Boolean(petId && userId), retry: false });
  // 标题名字和上面的 petId 同一来源（先名单、再家园快照），标题永远是正在看的那只（Q 10-03 跟进）
  const name = pet?.name ?? home.data?.pet.name;
  const chatOpen = section === "private" || section === "family";
  const pageRef = useRef<HTMLElement>(null);
  usePhoneListMemory(pageRef, section === "phone" && identityReady, userId, household?.household_id ?? null, petId ?? null, household?.home_id ?? null);
  const view = useChatViewport(chatOpen, pageRef);
  const fold = chatOpen ? foldFor(view.visibleHeight) : "none";
  // 告诉外壳软键盘开没开：外壳在键盘期间收起底栏（app/RootLayout + layout.css，做法 A）；离开通讯器时关掉
  const keyboardOpen = view.keyboard !== "none";
  useEffect(() => {
    setChatKeyboardOpen(keyboardOpen);
  }, [keyboardOpen]);
  useEffect(() => () => setChatKeyboardOpen(false), []);
  const [followToken, setFollowToken] = useState(0);
  // 标已读：最新一条真正显示在屏幕上（停在最底下、页面在前台）才发 POST /read；往上翻、在“TA 的朋友”分区都不发（见 ./useMarkRead）
  // 带上分区：只推进屏幕上这个分区的已读，另一个分区没看过的不跟着算已读（R26 第 1 条）；
  // 一个人住的家两条频道都在“我和 TA”里，看到最新一条就是都看过了，不带分区（后端两个分区一起推进）
  const markRead = useMarkRead({ userId, petId, channel: solo ? undefined : channel, enabled: identityReady && !thread.isError && chatOpen && Boolean(thread.data) });
  // 手机首屏每个聊天的未读（首页快照的分区未读；标已读的回包会把它改掉）
  const unreadPrivate = home.data?.unread.messages_private ?? 0;
  const unreadFamily = home.data?.unread.messages_family ?? 0;
  const unreadMine = solo ? unreadPrivate + unreadFamily : unreadPrivate;
  // 滚动位置只留本账号的；消息缓存被清掉（退出、换家）时一起清（见 chatMemory）
  useEffect(() => {
    keepOnlyUser(userId);
    forgetWithQueryCache(queryClient);
  }, [userId, queryClient]);
  const all = useMemo(() => (identityReady && !thread.isError && thread.data?.pet_id === petId && thread.data ? mergeThread(older.items, thread.data.items) : null), [identityReady, thread.isError, thread.data, petId, older.items]);
  // 家庭群按出现时刻排（排队的回复按 available_at；同一时刻保持原来的先后）；我和 TA 照旧按接口给的顺序
  const items = useMemo(() => {
    if (!all) return null;
    const list = all.filter((m) => solo || (m.channel ?? "private") === channel);
    return channel === "family" && !solo ? [...list].sort((a, b) => shownAt(a) - shownAt(b)) : list;
  }, [all, channel, solo]);
  const groupNow = useNow(60_000);
  // 新到的消息轻轻淡入：只认“这只宠物的对话第一次拿到时，比当时最新一条还新”的那几条（刚发出的、TA 刚回的）；
  // 第一次加载、往上翻出来的旧消息都比这个时刻早，不动（2026-09-29 美化；样式见 communicator.css 的 .ps-msg.is-fresh）
  const freshAfter = useRef<{ petId: string | null; at: number } | null>(null);
  if (all && freshAfter.current?.petId !== (petId ?? null)) freshAfter.current = { petId: petId ?? null, at: all.reduce((max, m) => Math.max(max, shownAt(m)), 0) };
  const isFresh = (m: MessageSummary) => freshAfter.current !== null && shownAt(m) > freshAfter.current.at;
  const topBar = (
    <TopBar
      // 手机首屏回到 TA 此刻的主屏（在家小屋、在外地图）；聊天、朋友回手机首屏（站内有上一页时都是退回上一页）
      back={section === "phone" ? primary.to : section === "petchats" && params.get("thread") ? PET_CHATS_HREF : PHONE_HOME}
      sourceBack={{ fallback: section === "phone" ? primary.to : section === "petchats" && params.get("thread") ? PET_CHATS_HREF : PHONE_HOME, levelParent: section === "phone" ? undefined : section === "petchats" && params.get("thread") ? PET_CHATS_HREF : PHONE_HOME }}
      title={
        home.data || pet ? (
          <CommTitle
            before={section === "private" ? "我和 " : ""}
            name={name ?? ""}
            after={section === "family" ? " 的家庭来信" : section === "friends" ? " 的朋友" : section === "phone" ? " 的手机" : section === "petchats" ? " 和朋友的聊天" : ""}
          />
        ) : section === "private" ? "我和 TA" : "TA 的手机"
      }
      subtitle={section === "family" ? undefined : section === "friends" ? "TA 自己在外面认识的朋友" : section === "petchats" ? "你在旁边看着，不插话" : section === "phone" ? undefined : "只有你和 TA 看得到"}
      right={
        section === "private" ? (
          <Link className="ps-btn ps-btn--ghost ps-btn--sm ps-comm-supplement" to="/onboarding/reception?mode=supplement">
            <Icon name="bookmark" size={16} /> 补充叮嘱
          </Link>
        ) : undefined
      }
    />
  );
  // 家庭群顶上：群头像、群名、“N 只伙伴 · M 位家人”（点开看群里都有谁）；我和 TA 顶上不再挂别的（公告、分区都在手机首屏）
  const extras =
    section === "family" ? <><FamilyGroupHeader householdName={group.householdName} pets={group.pets} people={group.people} onOpenMembers={() => setMembersOpen(true)} /></> : null;
  // 不用共享 Page：聊天页要把量出来的高度挂在 main 上（ref、样式变量、data-*）；类名照旧是 ps-page。
  return (
    <main
      ref={pageRef}
      className={`ps-page ps-comm-page${chatOpen ? " ps-comm-page--chat" : ""}`}
      data-keyboard={chatOpen ? view.keyboard : undefined}
      data-fold={chatOpen ? fold : undefined}
      style={chatOpen ? view.style : undefined}
    >
      {/* 矮屏（横屏、键盘弹起）时公告细条和分区切换挪进消息区最上面、跟着消息滚走；再矮连顶栏也挪进去（foldFor） */}
      {fold === "all" ? null : topBar}
      {fold === "none" ? extras : null}
      {section === "phone" ? (
        <PhoneHome
          readError={thread.error}
          retryRead={() => { void thread.refetch(); }}
          retrying={thread.isFetching}
          identityReady={identityReady}
          pet={pet ?? (home.data ? home.data.pet : null)}
          petId={pet?.pet_id ?? petId ?? null}
          name={name ?? "TA"}
          solo={solo}
          group={group}
          items={all}
          unreadMine={unreadMine}
          unreadFamily={solo ? 0 : unreadFamily}
        />
      ) : section === "petchats" ? (
        <PetChatsSection petId={pet?.pet_id ?? petId ?? null} name={name ?? null} threadId={params.get("thread")} />
      ) : section === "friends" ? <PetFriendList petId={pet?.pet_id ?? petId ?? null} /> : (
        <>
          <ChatScroller
            key={`${identityKey}|${channel}`}
            memoKey={chatMemoKey(userId ?? "-", petId ?? "-", channel, household?.household_id ?? null, household?.home_id ?? null)}
            items={items}
            followToken={followToken}
            lead={fold === "none" ? null : <>{fold === "all" ? topBar : null}{extras}</>}
            top={thread.data ? <OlderMarker status={older.status} hasMore={older.hasMore} empty={!items?.length} onLoad={older.retry} /> : null}
            onNearTop={older.loadMore}
            onSeenLatest={markRead}
          >
            <QueryView query={thread}>
              {(t: MessageThread) => (
                <>
                  <ul className="ps-msgs">
                    {(items ?? []).map((m) => (
                      <Fragment key={m.message_id}><Bubble privateGameContext={asked === "private" && section === "private" && channel === "private" && Boolean(userId && pet && household && t.pet_id === pet.pet_id)} photoAskStatus={asked === "private" && section === "private" && petId ? photoAskStatusFor(photoAsks.page, m, petId) : null} photoScope={`${identityKey}|${channel}`} onReadPhoto={() => readPhoto(m)} readingPhoto={thread.isFetching} m={m} fresh={isFresh(m)} channelTag={!solo && channel !== "family"} group={channel === "family" && !solo ? { name: name ?? "TA", now: groupNow } : undefined} reply={m.reply_to ? (all ?? t.items).find((candidate) => candidate.message_id === m.reply_to) ?? null : null} hasGuide={Boolean(m.source_event_id && guides.data?.some((guide) => guide.journey_id === m.source_event_id?.split(":", 1)[0]))} hasCollection={Boolean(m.source_event_id && collection.data?.some((item) => item.source_event_id === m.source_event_id))} />{channel === "private" && petId ? <ComeHomeConfirm message={m} userId={userId} petId={petId} name={name ?? "TA"} journey={home.data?.journey ?? null} timeZone={home.data?.place?.timezone ?? null} /> : null}</Fragment>
                    ))}
                  </ul>
                  {/* 第一次在私聊里用到 TA 的回信：一句用途说明和设置入口（REL-02 3.2；只在模型回信真的在用时出现，点过就不再出现） */}
                  {channel === "private" ? <ModelReplyNote userId={userId} petId={pet?.pet_id ?? petId ?? null} /> : null}
                  {items?.length || older.hasMore ? null : <EmptyState icon="mail" art={channel === "family" ? EMPTY_ART.family : EMPTY_ART.chat} title={channel === "family" ? "家里还没有共同来信" : "你和 TA 还没有私聊"}>{channel === "family" ? "TA 在旅途中写给全家的信，会收在这里。" : "给 TA 捎句话，回复会按照 TA 的状态稍后到达。"}</EmptyState>}
                  <DataOriginBadge origin={t.data_origin} />
                </>
              )}
            </QueryView>
          </ChatScroller>
          {identityReady && !thread.isError && thread.data ? (petId && channel === "private" ? <><ComeHomeBar userId={userId} petId={petId} name={name ?? "TA"} journey={home.data?.journey ?? null} timeZone={home.data?.place?.timezone ?? null} />{thread.data.pet_id === petId ? <details key={"game-invite:" + identityKey} className="ps-game-invite-launcher"><summary><Icon name="game" size={18} /><span>和 TA 下五子棋</span></summary><PrivateGameInviteButton onCreated={() => setFollowToken((n) => n + 1)} /></details> : null}<Composer key={"message-composer:" + identityKey} scope={identityKey} petId={petId} identity={{ userId: userId ?? null, householdId: household?.household_id ?? null, homeId: household?.home_id ?? null, petId }} onSent={() => setFollowToken((n) => n + 1)} /></> : channel === "family" ? <FamilyReadOnly name={name ?? "TA"} hasLetters={Boolean(items?.length)} /> : null) : null}
        </>
      )}
      {membersOpen && section === "family" ? <FamilyGroupMembers pets={group.pets} people={group.people} onClose={() => setMembersOpen(false)} /> : null}
    </main>
  );
}

/**
 * 一位朋友的详情（UF-01）：朋友编号来自地址；宠物与通讯器页同一个来源——家庭上下文里的那一只，没有时退回当前家园快照的那一只
 * （切宠时由外壳退回朋友列表，见 listForPetScopedPath）。宠物还在读时显示加载，不先说“这里看不到了”。
 */
function PetFriendDetailRoute() {
  const { friendId = "" } = useParams();
  const { pet } = useCurrentHousehold();
  const home = useActiveHome();
  const petId = pet?.pet_id ?? home.data?.pet.pet_id ?? null;
  return <PetFriendDetailPage petId={petId} friendId={friendId || null} pending={!petId && home.isPending} />;
}

export default defineModule({
  id: "communicator",
  // handle 标出这是聊天页：外壳据此在软键盘弹起时收起底栏（不按路径猜，见 shared/ui/chatShell）。
  routes: [{ path: "communicator", element: <CommunicatorPage />, handle: CHAT_SHELL_HANDLE }],
  // 公告页是二级页：全屏、不挂底栏，登录与入住守卫沿用 WorldGate（写法同 life 模块）。
  bareRoutes: [
    {
      path: "communicator/friends/:friendId",
      element: (
        <WorldGate>
          <PetFriendDetailRoute />
        </WorldGate>
      ),
    },
    {
      path: "events",
      element: (
        <WorldGate>
          <PlanetEventsPage />
        </WorldGate>
      ),
    },
    {
      path: "announcements",
      element: (
        <WorldGate>
          <AnnouncementsPage />
        </WorldGate>
      ),
    },
  ],
  services: {
    communicator: {
      fixture: () => {
        const sent: MessageSummary[] = [];
        return {
          // 演示会话只有一页：带 cursor 来就回空页、没有更早的了
          thread: async (petId, cursor) => {
            if (cursor) return delay({ pet_id: petId, items: [], next_cursor: null, data_origin: "fixture" as const });
            const t = fixtureThread(petId);
            return delay({ ...t, items: [...t.items, ...sent] });
          },
          send: async (_petId, body) => {
            const existing = sent.find((m) => m.client_message_id === body.client_message_id);
            if (existing) return delay(existing);
            const m: MessageSummary = { message_id: `fx-m-${Date.now()}`, client_message_id: body.client_message_id, sender: "owner", text: body.text, state: "delivered", created_at: new Date().toISOString(), photo_url: null };
            sent.push(m);
            return delay(m);
          },
          // 演示：已读只在这一页里算，不存；回包照契约的形状（演示里没有未读）
          markRead: async (petId, readThrough) => delay({ pet_id: petId, read_through: readThrough, unread: 0, server_time: new Date().toISOString() }),
          // 演示公告：标题带“（演示）”，公告页在演示模式下另挂“演示公告”标识（契约的 source 没有“演示”这一值）——不冒充真实运营公告。
          announcements: async () => delay(demoAnnouncementFeed()),
          // 演示星球活动：标题带“（演示）”，活动页另挂“演示活动”标识；“告诉 TA / 不告诉了”只记在本机内存里（见 events.service）。
          ...demoPlanetEventService(),
        };
      },
      live: ({ api }) => ({
        // 游标原样放进 ?cursor=（不解析）；不带 cursor 是最新一页
        thread: (petId, cursor) => api.request<MessageThread>(`/communicator/${encodeURIComponent(petId)}/messages`, cursor ? { query: { cursor } } : undefined),
        receipt: (petId, clientMessageId, signal) => api.request<MessageSummary>(`/communicator/${encodeURIComponent(petId)}/messages/by-client/${encodeURIComponent(clientMessageId)}`, { signal }),
        send: (petId, body) => api.request<MessageSummary>(`/communicator/${encodeURIComponent(petId)}/messages`, { method: "POST", body }),
        // read_through 原样放进请求体（不经过 Date）；幂等键由调用方每次新给
        markRead: (petId, readThrough, idempotencyKey, channel) =>
          api.request<MessageReadResult>(`/communicator/${encodeURIComponent(petId)}/read`, { method: "POST", body: channel ? { read_through: readThrough, channel } : { read_through: readThrough }, idempotencyKey }),
        // 契约已有 AnnouncementFeed，但路由还没挂 response_model（归运营后台），服务端暂不保证形状：
        // 先按 unknown 取回，再逐条解析成契约类型（坏条目丢掉，外层坏了抛可重试的错误）。
        announcements: async (signal) => parseAnnouncementFeed(await api.request<unknown>("/announcements", { signal })),
        ...livePlanetEventService(api),
      }),
    },
  },
});
